import React, { useEffect, useMemo, useState } from "react";
import Icons from "../icons";
import { DIALOG_HEIGHT } from "./dialogSize";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

function execStatusClasses(status) {
  switch ((status || "").toLowerCase()) {
    case "completed":
      return "bg-green-50 text-green-700 ring-green-600/20";
    case "in_progress":
      return "bg-blue-50 text-blue-700 ring-blue-600/20";
    default:
      return "bg-slate-100 text-slate-500 ring-slate-500/20";
  }
}

/**
 * Modal to link one or more existing executions to a ticket.
 *
 * Lists every execution; the ones already linked to this ticket are shown
 * checked + disabled. On save, the chosen (newly selected) execution keys are
 * handed up via onLink for the parent to persist.
 *
 * Props:
 *   ticket          : { key, summary }
 *   alreadyLinked   : Set<executionkey> already linked to this ticket
 *   onLink          : async (executionKeys[]) => void
 *   onClose         : () => void
 */
export default function LinkExecutionModal({ ticket, alreadyLinked, onLink, onClose }) {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [checked, setChecked] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");

  const linked = useMemo(() => alreadyLinked || new Set(), [alreadyLinked]);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}executions`);
        const body = await res.json().catch(() => ({}));
        if (active) setExecutions(Array.isArray(body?.data) ? body.data : []);
      } catch {
        if (active) setError("Could not load executions.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const toggle = (key) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return executions;
    return executions.filter(
      (e) =>
        (e.name || "").toLowerCase().includes(q) ||
        (e.description || "").toLowerCase().includes(q)
    );
  }, [executions, query]);

  const save = async () => {
    const keys = Array.from(checked);
    if (!keys.length) return;
    setSaving(true);
    setError("");
    try {
      await onLink?.(keys);
      onClose?.();
    } catch (e) {
      setError(e.message || "Could not link executions.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className={`flex ${DIALOG_HEIGHT} w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-slate-200`}>
        <div className="flex flex-none items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
              <Icons.Play className="h-4 w-4 text-brand-600" />
              Link execution
            </h2>
            <p className="mt-0.5 truncate text-sm text-slate-500">
              <span className="font-mono font-medium text-brand-700">{ticket?.key}</span>{" "}
              {ticket?.summary}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-none rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <Icons.X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <p className="mb-3 text-xs text-slate-500">
            Pick existing executions to link to this ticket. Executions already linked
            are checked and locked.
          </p>

          {executions.length > 0 && (
            <div className="relative mb-3">
              <Icons.Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter executions…"
                className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
          )}

          {loading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
              <Icons.Spinner className="h-4 w-4 animate-spin" />
              Loading executions…
            </div>
          ) : !filtered.length ? (
            <p className="rounded-lg bg-slate-50 px-3 py-8 text-center text-sm text-slate-400">
              {executions.length ? "No executions match your filter." : "No executions yet."}
            </p>
          ) : (
            <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
              {filtered.map((ex) => {
                const isLinked = linked.has(ex.executionkey);
                const isChecked = isLinked || checked.has(ex.executionkey);
                return (
                  <li key={ex.executionkey}>
                    <label
                      className={[
                        "flex items-center gap-3 px-3 py-2.5 text-sm",
                        isLinked ? "cursor-default opacity-70" : "cursor-pointer hover:bg-slate-50",
                      ].join(" ")}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        disabled={isLinked}
                        onChange={() => toggle(ex.executionkey)}
                        className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-slate-800">{ex.name}</span>
                        <span className="block text-xs text-slate-400">
                          {ex.total ?? 0} test{(ex.total ?? 0) === 1 ? "" : "s"}
                        </span>
                      </span>
                      <span
                        className={[
                          "flex-none rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ring-1 ring-inset",
                          execStatusClasses(ex.status),
                        ].join(" ")}
                      >
                        {(ex.status || "").replace("_", " ")}
                      </span>
                      {isLinked && (
                        <span className="flex-none text-[11px] font-medium text-emerald-600">
                          Linked
                        </span>
                      )}
                    </label>
                  </li>
                );
              })}
            </ul>
          )}

          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        </div>

        <div className="flex flex-none items-center justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || loading || checked.size === 0}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? (
              <>
                <Icons.Spinner className="h-4 w-4 animate-spin" />
                Linking…
              </>
            ) : (
              <>
                <Icons.Check className="h-4 w-4" />
                Link {checked.size || ""}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
